import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLocale } from '@/contexts/LocaleContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { getSamples, updateSampleStatus } from '@/lib/api';
import { 
  TestTube, 
  ArrowRight, 
  Clock,
  Building2,
  Package,
  CheckCircle2,
  XCircle,
  Truck
} from 'lucide-react';
import { toast } from 'sonner';

const getStatusLabels = (isUSA) => ({
  nueva: isUSA ? 'New' : 'Nueva',
  aceptada: isUSA ? 'Accepted' : 'Aceptada',
  rechazada: isUSA ? 'Rejected' : 'Rechazada',
  enviada: isUSA ? 'Shipped' : 'Enviada',
  entregada: isUSA ? 'Delivered' : 'Entregada'
});

const SampleList = () => {
  const [receivedSamples, setReceivedSamples] = useState([]);
  const [sentSamples, setSentSamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isUSA } = useLocale();
  const statusLabels = getStatusLabels(isUSA);

  useEffect(() => {
    fetchSamples();
  }, []);

  const fetchSamples = async () => {
    try {
      const [received, sent] = await Promise.all([
        getSamples('received'),
        getSamples('sent')
      ]);
      setReceivedSamples(received);
      setSentSamples(sent);
    } catch (error) {
      console.error('Error fetching samples:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (sampleId, status) => {
    try {
      await updateSampleStatus(sampleId, status);
      toast.success(isUSA ? 'Status updated' : 'Estado actualizado');
      fetchSamples();
    } catch (error) {
      toast.error(isUSA ? 'Error updating status' : 'Error al actualizar estado');
    }
  };

  const SampleCard = ({ sample, type }) => (
    <Card className="card-industrial" data-testid={`sample-card-${sample.sample_id}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Package className="w-4 h-4 text-slate-400" />
              <span className="font-medium text-slate-900">{sample.product_name}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Building2 className="w-4 h-4" />
              {type === 'received' ? sample.requester_company_name : sample.provider_company_name}
            </div>
            <p className="text-sm text-slate-500 mt-2">
              {isUSA ? 'Quantity: ' : 'Cantidad: '}{sample.quantity} {isUSA ? 'sample(s)' : 'muestra(s)'}
            </p>
            {sample.urgency === 'urgente' && (
              <Badge className="mt-2 bg-red-100 text-red-700">{isUSA ? 'Urgent' : 'Urgente'}</Badge>
            )}
          </div>
          <Badge className={`status-${sample.status}`}>
            {statusLabels[sample.status] || sample.status}
          </Badge>
        </div>

        {sample.shipping_address && (
          <p className="text-sm text-slate-500 mt-3">
            {isUSA ? 'Ship to: ' : 'Enviar a: '}{sample.shipping_address}
          </p>
        )}

        {(sample.sample_cost || sample.shipping_cost) && (
          <div className="mt-3 p-3 bg-slate-50 rounded-lg text-sm">
            {sample.sample_cost && <p>{isUSA ? 'Sample cost: ' : 'Costo de muestra: '}${sample.sample_cost} {isUSA ? 'USD' : 'MXN'}</p>}
            {sample.shipping_cost && <p>{isUSA ? 'Shipping cost: ' : 'Costo de envío: '}${sample.shipping_cost} {isUSA ? 'USD' : 'MXN'}</p>}
          </div>
        )}

        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center gap-1 text-xs text-slate-500">
            <Clock className="w-3 h-3" />
            {new Date(sample.created_at).toLocaleDateString(isUSA ? 'en-US' : 'es-MX')}
          </div>
          
          {type === 'received' && sample.status === 'nueva' && (
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => handleStatusUpdate(sample.sample_id, 'rechazada')}
                className="text-red-600 hover:text-red-700"
              >
                <XCircle className="w-4 h-4 mr-1" />
                {isUSA ? 'Reject' : 'Rechazar'}
              </Button>
              <Button 
                size="sm"
                onClick={() => handleStatusUpdate(sample.sample_id, 'aceptada')}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <CheckCircle2 className="w-4 h-4 mr-1" />
                {isUSA ? 'Accept' : 'Aceptar'}
              </Button>
            </div>
          )}

          {type === 'received' && sample.status === 'aceptada' && (
            <Button 
              size="sm"
              onClick={() => handleStatusUpdate(sample.sample_id, 'enviada')}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Truck className="w-4 h-4 mr-1" />
              {isUSA ? 'Mark as shipped' : 'Marcar enviada'}
            </Button>
          )}

          {type === 'sent' && sample.status === 'enviada' && (
            <Button 
              size="sm"
              onClick={() => handleStatusUpdate(sample.sample_id, 'entregada')}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <CheckCircle2 className="w-4 h-4 mr-1" />
              {isUSA ? 'Confirm received' : 'Confirmar recibida'}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="samples-page">
      <div className="mb-8">
        <h1 className="page-title">{isUSA ? 'Physical Samples' : 'Muestras Físicas'}</h1>
        <p className="page-subtitle">{isUSA ? 'Manage product sample requests' : 'Gestiona solicitudes de muestras de productos'}</p>
      </div>

      <Tabs defaultValue="received">
        <TabsList>
          <TabsTrigger value="received" data-testid="received-samples-tab">
            {isUSA ? 'Received' : 'Recibidas'} ({receivedSamples.length})
          </TabsTrigger>
          <TabsTrigger value="sent" data-testid="sent-samples-tab">
            {isUSA ? 'Requested' : 'Solicitadas'} ({sentSamples.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="received" className="mt-6">
          {receivedSamples.length === 0 ? (
            <div className="empty-state">
              <TestTube className="empty-state-icon" />
              <h3 className="empty-state-title">{isUSA ? 'No sample requests' : 'No hay solicitudes de muestras'}</h3>
              <p className="empty-state-text">
                {isUSA ? 'Sample requests you receive will appear here' : 'Las solicitudes de muestras que recibas aparecerán aquí'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {receivedSamples.map((sample) => (
                <SampleCard key={sample.sample_id} sample={sample} type="received" />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="sent" className="mt-6">
          {sentSamples.length === 0 ? (
            <div className="empty-state">
              <TestTube className="empty-state-icon" />
              <h3 className="empty-state-title">{isUSA ? "You haven't requested samples" : 'No has solicitado muestras'}</h3>
              <p className="empty-state-text">
                {isUSA ? 'Search for products and request physical samples' : 'Busca productos y solicita muestras físicas'}
              </p>
              <Link to="/search">
                <Button className="mt-4 bg-emerald-600 hover:bg-emerald-700">
                  {isUSA ? 'Search products' : 'Buscar productos'}
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {sentSamples.map((sample) => (
                <SampleCard key={sample.sample_id} sample={sample} type="sent" />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SampleList;
